/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useRef, useState } from "react";
import { Dialog, Transition, Switch } from "@headlessui/react";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import {
  DEFAULT_TEMPLATE_TITLE,
  DEFAULT_TEMPLATE_WIDTH,
  DEFAULT_TEMPLATE_HEIGHT,
  MAX_TEMPLATE_WIDTH,
  MAX_TEMPLATE_HEIGHT,
} from "@utils/constant";
import CopyButton from "../shared/CopyButton";

type TemplateInfo = {
  id?: string;
  title: string;
  width: number;
  height: number;
  public: boolean;
};

function TemplateSettingsModal({
  header,
  templateInfo,
  open = false,
  onCancel = () => {},
  onSubmit = () => {},
}: {
  header: string;
  templateInfo?: TemplateInfo;
  open?: boolean;
  onCancel?: () => void;
  onSubmit?: (templateInfo: TemplateInfo) => void;
}) {
  const { t } = useTranslation();
  const cancelButtonRef = useRef(null);
  const templateId = templateInfo?.id || "";
  const [title, setTitle] = useState(
    templateInfo?.title || DEFAULT_TEMPLATE_TITLE
  );
  const [width, setWidth] = useState(
    templateInfo?.width || DEFAULT_TEMPLATE_WIDTH
  );
  const [height, setHeight] = useState(
    templateInfo?.height || DEFAULT_TEMPLATE_HEIGHT
  );
  const [isPublic, setIsPublic] = useState(templateInfo?.public || false);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={onCancel}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="px-6 pt-8 pb-6">
                <div className="text-center mb-8">
                  <Dialog.Title
                    as="h1"
                    className="text-2xl font-bold text-gray-900 mb-2"
                  >
                    {header}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500">
                    {t("template_settings_body")}
                  </p>
                </div>
                <div className="space-y-6">
                  {templateId !== "" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("template_settings_id_label")}
                      </label>
                      <div className="mb-3 flex items-start rounded-lg bg-blue-50 p-3 border border-blue-200">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-2 h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="text-sm text-blue-700">
                          <Trans i18nKey="template_settings_id_help">
                            Use this ID to reference your template in API calls.
                            Check{" "}
                            <a
                              href="https://docs.copicake.com/#/api/v1/image/create"
                              className="font-medium underline hover:no-underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              our documentation
                            </a>{" "}
                            to learn more.
                          </Trans>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm focus:outline-none"
                          type="text"
                          disabled
                          value={templateId}
                        />
                        <CopyButton
                          text={templateId}
                          onCopy={() => {
                            toast.success("Template ID copied to clipboard");
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("template_settings_title_label")}
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      type="text"
                      placeholder={DEFAULT_TEMPLATE_TITLE}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("template_settings_width_label")}
                      </label>
                      <input
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        type="number"
                        placeholder={t("template_settings_width_placeholder")}
                        value={width}
                        onChange={(e) => setWidth(parseInt(e.target.value))}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {t("template_settings_max_px", { value: MAX_TEMPLATE_WIDTH })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("template_settings_height_label")}
                      </label>
                      <input
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        type="number"
                        placeholder={t("template_settings_height_placeholder")}
                        value={height}
                        onChange={(e) => setHeight(parseInt(e.target.value))}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {t("template_settings_max_px", { value: MAX_TEMPLATE_HEIGHT })}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("template_settings_public_label")}
                        </label>
                        <p className="text-sm text-gray-500 mt-1">
                          {t("template_settings_public_help")}
                        </p>
                      </div>
                      <Switch
                        checked={isPublic}
                        onChange={setIsPublic}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          isPublic ? "bg-blue-500" : "bg-gray-200"
                        }`}
                      >
                        <span className="sr-only">
                          {t("template_settings_public_sr")}
                        </span>
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isPublic ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </Switch>
                    </div>
                    {isPublic && (
                      <div className="mt-3 rounded-lg bg-amber-50 p-3 border border-amber-200">
                        <div className="flex">
                          <svg className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="ml-2">
                            <p className="text-sm text-amber-700">
                              <Trans
                                i18nKey="template_settings_public_warning"
                                components={{ strong: <strong /> }}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex gap-3 rounded-b-xl">
                <button
                  type="button"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  onClick={() => {
                    onCancel();
                  }}
                  ref={cancelButtonRef}
                >
                  {t("common_cancel")}
                </button>
                <button
                  type="button"
                  className="flex-1 rounded-lg bg-blue-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  onClick={() => {
                    onSubmit({
                      width,
                      height,
                      title,
                      public: isPublic,
                    });
                  }}
                >
                  {t("common_save_changes")}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default TemplateSettingsModal;
